import { cn } from "@/shared/lib/utils";
import type { ButtonProps } from "../button";
import { Button } from "../button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../dialog";

type DialogProps = React.ComponentPropsWithoutRef<typeof Dialog>;
type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogContent>;
type DialogTitleProps = React.ComponentPropsWithoutRef<typeof DialogTitle>;
interface ModalRootProps extends DialogProps {
  children: React.ReactNode;
}

const ModalRoot = ({ children, ...props }: ModalRootProps) => {
  return <Dialog {...props}>{children}</Dialog>;
};

interface ModalTriggerProps extends ButtonProps {
  asChild?: boolean;
  children: React.ReactNode;
}

const ModalTrigger = ({
  asChild,
  children,
  ...buttonProps
}: ModalTriggerProps) => {
  const buttonPropsWithoutAsChild: Omit<ButtonProps, "asChild"> = buttonProps;
  return (
    <DialogTrigger>
      {asChild ? (
        children
      ) : (
        <Button
          variant="outline"
          className="h-full text-textColor"
          {...buttonPropsWithoutAsChild}
        >
          {children}
        </Button>
      )}
    </DialogTrigger>
  );
};

interface ModalTitleProps extends DialogTitleProps {
  children: React.ReactNode;
}

const ModalTitle = ({ children, className, ...props }: ModalTitleProps) => {
  return (
    <DialogHeader>
      <DialogTitle
        className={cn("text-2xl font-bold text-textColor", className)}
        {...props}
      >
        {children}
      </DialogTitle>
    </DialogHeader>
  );
};

interface ModalCloseProps {
  children?: React.ReactNode;
}

const ModalClose = ({ children }: ModalCloseProps) => {
  return (
    <div className="flex w-full flex-1 flex-col items-end justify-end">
      <DialogClose asChild>
        <Button
          className="w-full font-medium"
          type="button"
          variant="secondary"
        >
          {children ?? "Отмена"}
        </Button>
      </DialogClose>
    </div>
  );
};

interface ModalContentProps extends DialogContentProps {
  children: React.ReactNode;
}

const ModalContent = ({ children, className, ...props }: ModalContentProps) => {
  return (
    <DialogContent className={cn(className, "flex-1")} {...props}>
      {children}
    </DialogContent>
  );
};

export const Modal = Object.assign(ModalRoot, {
  Trigger: ModalTrigger,
  Title: ModalTitle,
  Content: ModalContent,
  Close: ModalClose,
});
